import { buildAbility, ability } from "@/api/models/auth/ability";
import JwtService, { type TAuthTokens, type TCredentials } from "@/api/models/auth/jwt";
import type { TSsoTokens } from "@/api/models/auth/sso/types";
import UsersService, { type TUser } from "@/api/models/auth/users";
import { jwtDecode } from "jwt-decode";
import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useSnackStore } from "./snackStore";
import { useWebsocket } from "@/composables/models/websocket/useWebsocket";
import type { TLoginHintBadge } from "@/api/models/user/loginHintBadge";
import { type TPracticeStaff } from "@/api/models/practice/practiceStaff";
import { getPrimaryEmail } from "@/api/shared/contacts";
import { useStaffSubscription } from "@/composables/models/websocket/subscriptions/staffSubscription";
import type { TNotification } from "@/api/models/message/notification";
import { updateUnreadNotificationCount } from "@/composables/models/notification/useNotification";
import { AppError } from "@/utils/AppError";
import * as Sentry from "@sentry/vue";
import { queryClient } from "@/lib/queryClient";
import { usePendingTermsStore } from "@/stores/pendingTermsStore";
import { resetChatDockSearch } from "@/composables/models/message/useChatDock";

const TOKEN_VERIFICATION_INTERVAL = 2 * 60 * 1000;
const TOKEN_REFRESH_THRESHOLD = 0.85;
const REFRESH_TIMEOUT = 10 * 1000;
const CLOCK_SKEW_S = 30;

let storageLogoutHandledThisTab = false;

// Per-load state, deliberately not persisted: a reload re-evaluates this module,
// so the first `ensureAuthReady` of every load goes to the network.
let meRevalidatedThisLoad = false;
let meFetchInFlight: Promise<void> | null = null;

/** Models a fresh page load in tests, which share one module instance. */
export const __resetAuthLoadState = () => {
  meRevalidatedThisLoad = false;
  meFetchInFlight = null;
};

export const useAuthStore = defineStore(
  "auth",
  () => {
    type TAuthTransition =
      | {
          type: "idle";
        }
      | {
          type: "switching-role";
          targetLabel: string;
          startedAt: number;
        };

    const authTransition = ref<TAuthTransition>({ type: "idle" });

    const isRoleSwitching = computed(() => authTransition.value.type === "switching-role");

    const roleSwitchLabel = computed(() => {
      return authTransition.value.type === "switching-role"
        ? `Switching role to ${authTransition.value.targetLabel}...`
        : "Switching role...";
    });

    const beginRoleSwitch = (label: string) => {
      authTransition.value = {
        type: "switching-role",
        targetLabel: label,
        startedAt: Date.now(),
      };
    };

    const finishRoleSwitch = () => {
      authTransition.value = { type: "idle" };
    };

    const authUser = ref<TUser | null>(null);
    const loggedAsStaff = ref<TPracticeStaff | undefined>();

    const abilityRules = ref<any[]>([]);
    const accessToken = ref<string | null>(null);
    const accessTokenBorn = ref<number | null>(null);
    const refreshToken = ref<string | null>(null);
    const ssoLogoutUrl = ref<string | null>(null);
    const tokenVerificationIntervalId = ref<number | null>(null);
    const isRefreshing = ref(false);
    const refreshPromise = ref<Promise<string | null> | null>(null);
    const refreshFailureCount = ref(0);
    const lastRefreshFailure = ref(0);
    const refreshRetryTimeoutId = ref<number | null>(null);
    // Bumped every time the session is cleared. An in-flight refresh captures
    // the epoch before its network call and discards its result if the epoch
    // changed meanwhile — i.e. the user logged out while it was pending.
    const sessionEpoch = ref(0);

    const isLoggedIn = computed(() => !isAccessTokenExpired());

    const isSessionReady = computed(() => isLoggedIn.value && !!authUser.value?.logged_staff);

    const isImpersonating = computed(() => {
      if (!authUser.value?.logged_staff || !authUser.value?.master_staff) {
        return false;
      }
      return authUser.value.logged_staff.id !== authUser.value.master_staff.id;
    });

    const defaultsFormatters = {
      date: "MMMM D, YYYY",
      datetime: "MMMM D, YYYY hh:mm A",
    };

    const snackStore = useSnackStore();
    const router = useRouter();
    const websocket = useWebsocket();
    const route = useRoute();
    const { subscribeStaff, unsubscribeStaff } = useStaffSubscription();

    const startSession = async (tokens: TAuthTokens) => {
      storageLogoutHandledThisTab = false;
      // New identity — don't inherit the previous session's revalidation state.
      meRevalidatedThisLoad = false;
      meFetchInFlight = null;

      accessToken.value = tokens.access;
      accessTokenBorn.value = Date.now();
      refreshToken.value = tokens.refresh;

      await setMe(true);
      setupWebSocketConnection();
      tokenVerificationInterval("start");
    };

    const login = async (credentials: TCredentials, redirectToDashboard: boolean = false) => {
      ssoLogoutUrl.value = null;
      try {
        const authResponse = await JwtService.authenticate(credentials);
        await startSession(authResponse.data);
        if (redirectToDashboard) {
          await router.push("/");
        }
      } catch (error) {
        logError(error, {
          context: { feature: "auth", step: "login" },
        });
        await logout(undefined, true);
        return Promise.reject(error);
      }
    };

    // Unlike `login`, a failure stays on the current page so the SSO callback can explain it.
    const loginWithTokens = async (tokens: TSsoTokens) => {
      try {
        await startSession(tokens);
        ssoLogoutUrl.value = tokens.logout_url || null;
      } catch (error) {
        logError(error, {
          context: { feature: "auth", step: "loginWithTokens" },
        });
        await clearSession({ dontShowLogout: true, redirectToLogin: false });
        throw error;
      }
    };

    const getTokenData = (token: string | null) => {
      if (!token) {
        return null;
      }

      try {
        const jwt = jwtDecode(token);
        const now = Date.now() / 1000;
        return { jwt, now };
      } catch (error) {
        logError(error, {
          context: { feature: "auth", step: "getTokenData" },
        });
        return null;
      }
    };

    const isTokenExpired = (token: string | null) => {
      const tokenData = getTokenData(token);
      if (!tokenData) {
        return true;
      }

      const { jwt, now } = tokenData;
      // Coerce defensively: a numeric value or numeric string works as before,
      // but a missing or non-coercible exp must read as expired rather than
      // letting `exp < now` evaluate to NaN → false and pass a junk token as a
      // live session. (A strict typeof check would wrongly reject a perfectly
      // valid numeric-string exp and lock users out — don't do that.)
      const exp = Number(jwt.exp);
      if (!jwt.exp || Number.isNaN(exp)) {
        return true;
      }
      return exp < now + CLOCK_SKEW_S;
    };
    const isAccessTokenExpired = () => {
      return isTokenExpired(accessToken.value);
    };

    const needAccessTokenRefresh = () => {
      const tokenData = getTokenData(accessToken.value);
      if (!tokenData) {
        return true;
      }

      const { jwt, now } = tokenData;

      if (!jwt.iat || !jwt.exp) {
        return true;
      }

      const threshold = jwt.iat + (jwt.exp - jwt.iat) * TOKEN_REFRESH_THRESHOLD;
      return now > threshold;
    };

    const validateRefreshToken = () => {
      if (!refreshToken.value)
        throw new AppError("No refresh token available", {
          context: { feature: "auth", step: "validateRefreshToken", reason: "missing-refresh" },
        });
      const rt = jwtDecode(refreshToken.value);
      if (!rt.exp || rt.exp < Math.floor(Date.now() / 1000)) {
        throw new AppError("Refresh token expired", {
          context: { feature: "auth", step: "validateRefreshToken", reason: "refresh-expired" },
        });
      }
    };

    const refreshTokenIfNeeded = async (forceRefresh: boolean = false): Promise<string | null> => {
      if (isRefreshing.value && refreshPromise.value) {
        return refreshPromise.value;
      }

      // If a scheduled retry is already pending, don't pile on another attempt
      if (refreshRetryTimeoutId.value !== null) {
        return accessToken.value;
      }

      if (!forceRefresh && (!needAccessTokenRefresh() || !refreshToken.value)) {
        return accessToken.value;
      }

      try {
        validateRefreshToken();
      } catch (error) {
        logError(error, {
          context: {
            feature: "auth",
            step: "refreshTokenIfNeeded",
            reason: "token-refresh-failed",
          },
        });
        await logout("Session expired. Please log in again.");
        return null;
      }

      isRefreshing.value = true;

      refreshPromise.value = (async () => {
        let refreshTimeoutId: number | undefined;
        const epoch = sessionEpoch.value;
        try {
          const timeoutPromise = new Promise<never>((_, reject) => {
            refreshTimeoutId = window.setTimeout(
              () => reject(new Error("Refresh timeout")),
              REFRESH_TIMEOUT
            );
          });

          const authResponse = await Promise.race([
            JwtService.refresh(refreshToken.value!),
            timeoutPromise,
          ]);
          window.clearTimeout(refreshTimeoutId);

          // The session was cleared (logout) while this refresh was in flight —
          // the response is stale. Discard it instead of reviving a session the
          // user has already ended.
          if (sessionEpoch.value !== epoch) {
            return null;
          }

          const newAccess = authResponse.data.access;
          // A 200 carrying a missing/empty/undecodable access token is a
          // malformed response we cannot act on. Log out rather than storing an
          // unusable token (which would leave the user in a silent dead state).
          if (!newAccess || isTokenExpired(newAccess)) {
            await logout("Session expired. Please log in again.");
            return null;
          }

          accessToken.value = newAccess;
          accessTokenBorn.value = Date.now();
          refreshToken.value = authResponse.data.refresh;

          refreshFailureCount.value = 0;

          await websocket.updateConnection(accessToken.value);
          return accessToken.value;
        } catch (error: any) {
          window.clearTimeout(refreshTimeoutId);
          logError(error, {
            context: {
              feature: "auth",
              step: "refreshTokenIfNeeded",
              reason: "token-refresh-failed",
            },
          });
          refreshFailureCount.value++;
          lastRefreshFailure.value = Date.now();

          // A 401 from the refresh endpoint means the server has rejected the
          // token — log out immediately rather than retrying forever.
          if (error?.response?.status === 401) {
            await logout("Session expired. Please log in again.");
            return null;
          }

          // Only log out when the refresh token itself is locally expired.
          // Network failures, timeouts, and cancelled requests should
          // trigger a backoff retry — not an immediate logout.
          let refreshExpired = false;
          try {
            validateRefreshToken();
          } catch {
            refreshExpired = true;
          }

          if (refreshExpired) {
            await logout("Session expired. Please log in again.");
            return null;
          }

          scheduleRefreshRetry();
          return accessToken.value;
        } finally {
          isRefreshing.value = false;
          refreshPromise.value = null;
        }
      })();

      return refreshPromise.value;
    };

    // Exponential backoff: 2 s → 4 s → 8 s → 16 s → 30 s (capped)
    const scheduleRefreshRetry = () => {
      if (refreshRetryTimeoutId.value !== null) {
        window.clearTimeout(refreshRetryTimeoutId.value);
      }
      const jitter = Math.floor(Math.random() * 250);
      const delay = Math.min(1000 * Math.pow(2, refreshFailureCount.value), 30 * 1000) + jitter;

      refreshRetryTimeoutId.value = window.setTimeout(async () => {
        refreshRetryTimeoutId.value = null;
        await refreshTokenIfNeeded(true);
      }, delay);
    };

    const setMe = async (updateMe: boolean = false) => {
      if (updateMe) {
        // Coalesce concurrent callers onto one request.
        if (meFetchInFlight) {
          await meFetchInFlight;
        } else {
          const epoch = sessionEpoch.value;
          meFetchInFlight = (async () => {
            const me = await UsersService.me.get();
            // Session cleared while in flight — applying this would revive it.
            if (sessionEpoch.value !== epoch) {
              return;
            }
            authUser.value = me.data;
            loggedAsStaff.value = me.data.logged_staff;
          })();

          try {
            await meFetchInFlight;
            // Only a completed fetch counts, so a failure retries next call.
            meRevalidatedThisLoad = true;
          } finally {
            meFetchInFlight = null;
          }
        }
      }

      if (!authUser.value) {
        throw new AppError("You must be logged in.", {
          context: { feature: "auth", step: "setMe", reason: "no-user" },
        });
      }

      if (!authUser.value.logged_staff) {
        throw new AppError("You must be logged in as staff.", {
          context: { feature: "auth", step: "setMe", reason: "no-staff" },
        });
      }

      const permissions = authUser.value.logged_staff.permissions || [];
      const newAbility = buildAbility(permissions);
      abilityRules.value = newAbility.rules;
      ability.update(newAbility.rules);
    };

    const restoreAbilityRules = () => {
      if (abilityRules.value.length > 0) {
        ability.update(abilityRules.value);
      } else if (authUser.value?.logged_staff?.permissions) {
        const newAbility = buildAbility(authUser.value.logged_staff.permissions || []);
        ability.update(newAbility.rules);
        abilityRules.value = newAbility.rules;
      }
    };

    // Makes the page load authoritative about who the user is.
    //
    // The CASL singleton isn't persisted (a fresh load starts empty), while
    // `authUser`/`abilityRules` are — so a reload could otherwise serve an
    // identity written by an earlier session. Nothing in the token says which
    // staff is being impersonated; only /me does. Hence: refetch once per load,
    // then just re-apply the rules to the singleton.
    const ensureAuthReady = async () => {
      if (!isLoggedIn.value) return;

      if (!meRevalidatedThisLoad || !authUser.value) {
        // setMe(true) fetches /me and rebuilds the ability in one call.
        await setMe(true);
        return;
      }

      restoreAbilityRules();
    };

    const explicitLogout = ref(false);

    const clearSession = async ({
      withErrorMessage,
      dontShowLogout = false,
      redirectToLogin = true,
      preserveTransition = false,
      loginRedirect,
    }: {
      withErrorMessage?: string;
      dontShowLogout?: boolean;
      redirectToLogin?: boolean;
      preserveTransition?: boolean;
      loginRedirect?: string;
    } = {}) => {
      if (!preserveTransition) {
        authTransition.value = { type: "idle" };
      }

      storageLogoutHandledThisTab = true;
      // Invalidate any in-flight token refresh (and /me fetch) so a late
      // response can't revive this session after it's been cleared.
      sessionEpoch.value++;
      // The next session must re-establish its identity from the server.
      meRevalidatedThisLoad = false;
      meFetchInFlight = null;

      await closeWebSocketConnection();
      tokenVerificationInterval("stop");

      if (refreshRetryTimeoutId.value !== null) {
        window.clearTimeout(refreshRetryTimeoutId.value);
        refreshRetryTimeoutId.value = null;
      }

      authUser.value = null;
      accessToken.value = null;
      accessTokenBorn.value = null;
      refreshToken.value = null;
      ssoLogoutUrl.value = null;
      isRefreshing.value = false;
      refreshPromise.value = null;
      refreshFailureCount.value = 0;
      lastRefreshFailure.value = 0;
      loggedAsStaff.value = undefined;

      abilityRules.value = [];
      ability.update([]);

      resetChatDockSearch();
      // Pending terms belong to the session that just ended. Leaving the block set would gate every
      // query behind `useQueryEnabled` and re-open the acceptance dialog for whoever logs in next.
      usePendingTermsStore().clearBlock();
      // The cache outlives the session otherwise: releasing the terms block re-enables the queries
      // it was gating, which then refetch with no token and 401.
      queryClient.clear();
      updateSentryContext(null);

      if (withErrorMessage) {
        snackStore.showError(withErrorMessage);
      } else if (!dontShowLogout) {
        snackStore.showSuccess("You have been logged out.");
      }

      if (redirectToLogin) {
        await router.push(
          loginRedirect
            ? { path: "/auth/login", query: { redirect: loginRedirect } }
            : "/auth/login"
        );
      }
    };

    const logout = async (
      withErrorMessage?: string,
      dontShowLogout = false,
      loginRedirect?: string
    ) => {
      await clearSession({
        withErrorMessage,
        dontShowLogout,
        redirectToLogin: true,
        loginRedirect,
      });
    };

    // Only for a logout the user asked for: an automatic one (expiry, 401, another tab) must not
    // bounce them through the identity provider.
    const userLogout = async () => {
      const endSessionUrl = ssoLogoutUrl.value;
      if (!endSessionUrl) {
        await logout();
        return;
      }
      await clearSession({ dontShowLogout: true, redirectToLogin: false });
      globalThis.location.href = endSessionUrl;
    };

    const switchToLoginHintBadge = async (badge: TLoginHintBadge) => {
      beginRoleSwitch(badge.name);

      try {
        await clearSession({
          dontShowLogout: true,
          redirectToLogin: true,
          preserveTransition: true,
        });

        storageLogoutHandledThisTab = false;

        await login(
          {
            email: badge.email,
            password: badge.password,
          },
          true
        );
      } catch (error) {
        finishRoleSwitch();

        logError(error, {
          context: {
            feature: "auth",
            step: "switchToLoginHintBadge",
            reason: "switch-failed",
            badgeId: badge.id,
            badgeName: badge.name,
          },
        });

        await clearSession({
          withErrorMessage: "Unable to switch user. Please try again.",
          dontShowLogout: true,
          redirectToLogin: true,
          preserveTransition: false,
        });

        return Promise.reject(error);
      }
    };

    const setupWebSocketConnection = () => {
      if (!isLoggedIn.value || !accessToken.value) {
        return;
      }

      websocket.setupConnection(accessToken.value);
    };

    const closeWebSocketConnection = async () => {
      await websocket.disconnect();
    };

    const tokenVerificationInterval = (action: "start" | "stop") => {
      if (action === "start") {
        if (tokenVerificationIntervalId.value !== null) {
          window.clearInterval(tokenVerificationIntervalId.value);
        }

        tokenVerificationIntervalId.value = window.setInterval(async () => {
          // If a backoff retry is already scheduled, let it fire naturally
          if (refreshRetryTimeoutId.value !== null) return;

          if (refreshToken.value && !isTokenExpired(refreshToken.value)) {
            void refreshTokenIfNeeded();
          } else {
            await logout("Session expired. Please log in again.");
          }
        }, TOKEN_VERIFICATION_INTERVAL);
      } else if (action === "stop" && tokenVerificationIntervalId.value !== null) {
        window.clearInterval(tokenVerificationIntervalId.value);
        tokenVerificationIntervalId.value = null;
      }
    };

    const toSentryIdentity = (staff: TPracticeStaff) => ({
      id: staff.id,
      username: `${staff.first_name || ""} ${staff.last_name || ""}`.trim() || "Unknown",
      email: getPrimaryEmail(staff.emails)?.email || undefined,
    });

    const updateSentryContext = (staff: TPracticeStaff | undefined | null) => {
      const identity = staff ? toSentryIdentity(staff) : undefined;
      Sentry.setUser(identity ?? null);

      const master = staff && isImpersonating.value ? authUser.value?.master_staff : undefined;
      const impersonator = master ? toSentryIdentity(master) : undefined;
      const impersonated = impersonator ? identity : undefined;

      Sentry.setTags({
        impersonation: staff ? !!impersonator : undefined,
        "impersonation.loggedStaffId": impersonated?.id,
        "impersonation.loggedStaffName": impersonated?.username,
        "impersonation.loggedStaffEmail": impersonated?.email,
        "impersonation.masterStaffId": impersonator?.id,
        "impersonation.masterStaffName": impersonator?.username,
        "impersonation.masterStaffEmail": impersonator?.email,
      });
    };

    watch(
      [loggedAsStaff, isLoggedIn],
      async ([newLoggedStaff, _], [oldLoggedStaff, __]) => {
        if (oldLoggedStaff?.id && newLoggedStaff?.id !== oldLoggedStaff?.id) {
          unsubscribeStaff(oldLoggedStaff.id);
        }

        if (newLoggedStaff?.id && newLoggedStaff.id !== oldLoggedStaff?.id && isLoggedIn.value) {
          tokenVerificationInterval("start");
          setupWebSocketConnection();

          subscribeStaff(newLoggedStaff.id, async (event: TNotification) => {
            const pagePatientId = (route.params as { id?: string }).id;
            const notificationPatientId = event.patient ?? event.data?.patient ?? null;

            if (
              route.name === "/notificationsCenter" ||
              (pagePatientId && notificationPatientId && pagePatientId === notificationPatientId)
            ) {
              return;
            }

            await updateUnreadNotificationCount(queryClient);
          });
        }

        // Always sync Sentry context on change
        updateSentryContext(isLoggedIn.value ? newLoggedStaff : null);
      },
      { immediate: true }
    );

    const handleStorageChange = async (e: StorageEvent) => {
      if (e.key !== "auth") return;

      try {
        const previousStaffId = loggedAsStaff.value?.id;

        const store = useAuthStore();
        store.$hydrate({ runHooks: false });

        if (!accessToken.value && !refreshToken.value) {
          if (isRoleSwitching.value) {
            return;
          }

          // On a public page there is no protected content to clear, and a
          // logout would needlessly redirect to /auth/login and interrupt an
          // anonymous flow — leave this tab where it is.
          if (route.meta.requiresAuth === false) {
            return;
          }

          if (!storageLogoutHandledThisTab) {
            storageLogoutHandledThisTab = true;
            await logout(undefined);
          }

          return;
        }

        // Only reload on a genuine identity switch (was one staff, now another).
        // Don't reload when this tab simply goes from logged-out to logged-in
        // because the user logged in elsewhere.
        if (previousStaffId && loggedAsStaff.value?.id !== previousStaffId) {
          window.location.reload();
          return;
        }

        if (accessToken.value && websocket.isConnected.value) {
          await websocket.updateConnection(accessToken.value);
        }
      } catch (error) {
        logError(error, {
          context: {
            feature: "auth",
            step: "handleStorageChange",
            reason: "error-parsing-auth-store",
          },
        });
      }
    };
    // Add storage listener for cross-tab communication
    if (typeof window !== "undefined") {
      window.addEventListener("storage", handleStorageChange);
    }

    return {
      authUser,
      loggedAsStaff,
      // Returned so Pinia persists it. Without it the ability rules are rebuilt from
      // `authUser.logged_staff.permissions` on every load, which the synchronous router guard
      // races: a guard running before `authUser` hydrates evaluates an empty ability and renders
      // Unauthorized for a user who has the privileges.
      abilityRules,
      isLoggedIn,
      isSessionReady,
      isImpersonating,
      accessToken,
      accessTokenBorn,
      refreshToken,
      ssoLogoutUrl,
      isRefreshing,

      roleSwitchLabel,
      isRoleSwitching,
      switchToLoginHintBadge,
      finishRoleSwitch,

      login,
      loginWithTokens,
      logout,
      userLogout,
      explicitLogout,
      refreshTokenIfNeeded,
      setMe,
      ensureAuthReady,

      // setupWebSocketConnection,
      // closeWebSocketConnection,

      tokenVerificationInterval,

      defaultsFormatters,

      restoreAbilityRules,
    };
  },
  {
    persist: true,
  }
);

export enum ERestrictedSections {
  ADMINISTRATION = "administration",
  PLATFORM_CONTROL = "platform_control",
}
